/**
 * Client-side day totals (sets, reps, volume load) for non-date-based program days.
 *
 * Date-based calendars get their day totals from the API and refresh them via
 * `refetchCalendarDayTotals`. Programs can't: `totalVolumeLoad` is only ever
 * computed by the backend's date-range calendar-programming service, and the
 * saved-workouts endpoint that serves programs returns day items without the
 * field at all. So they're derived here, from the exercises already in Redux —
 * which also makes them update live on every edit, including the paths where the
 * reducer doesn't recompute them (a cross-day move re-sets only the destination
 * day's exercises and never touches the source day's aggregates).
 *
 * Volume mirrors the backend's AssignedWorkoutItem.setTotalVolumeLoad() for the
 * *no-athlete-max* case, which is the only case a program can be in: the API
 * only resolves percentage-of-max prescriptions when a request carries an
 * athleteId, and a program is never scoped to an athlete. That is why a
 * percentage-based set contributes 0 here rather than resolving a max — it is
 * matching the server, not missing a case. Don't "fix" it without a program
 * concept of whose max to use.
 *
 * Sets and reps mirror SavedWorkoutItem.setRepAndSetCount(): a plain sum of the
 * positive per-exercise counts, with no Track Volume Load gate. That asymmetry
 * with volume is the backend's, and keeping it means these derived numbers equal
 * the API's on an untouched day.
 */

/**
 * `prescribedType` values say what an exercise's primary prescribed value
 * means. Only PrimaryValue carries a load; the others describe reps, rest or
 * intensity and must not be multiplied into volume. A falsy `prescribedType` is
 * treated as PrimaryValue, matching the backend.
 */
const PRESCRIBED_TYPE_PRIMARY_VALUE = 1;

/**
 * Prescribed volume load for a single set: reps × load.
 *
 * @param {{ reps?: number|string, percent?: number|string,
 *   prescribedType?: number|string, prescribedValue?: number|string }} set
 * @returns {number} 0 when the set has no positive reps or no absolute load.
 */
const getSetVolumeLoad = (set) => {
  const reps = parseInt(set?.reps, 10);
  if (!reps || reps <= 0) return 0;

  /** Percentage-of-max needs an athlete max, which a program has no way to pick. */
  if (set?.percent) return 0;

  const isPrimaryValue = !set?.prescribedType
    || parseInt(set.prescribedType, 10) === PRESCRIBED_TYPE_PRIMARY_VALUE;
  if (!isPrimaryValue || !set?.prescribedValue) return 0;

  return (parseFloat(set.prescribedValue) || 0) * reps;
};

/**
 * Prescribed volume load for one exercise, summed across its sets. Exercises
 * without Track Volume Load enabled are excluded entirely.
 *
 * @param {{ trackVolumeLoad?: boolean, sets?: Array<Object> }} exercise
 * @returns {number}
 */
const getExerciseVolumeLoad = (exercise) => {
  if (!exercise?.trackVolumeLoad) return 0;
  return (exercise.sets || []).reduce((sum, set) => sum + getSetVolumeLoad(set), 0);
};

/** Sum a positive numeric field across a day's exercises, matching the backend's filter. */
const sumExerciseField = (exercises, field) => exercises
  .filter((exercise) => exercise?.[field] > 0)
  .reduce((sum, exercise) => sum + exercise[field], 0);

/**
 * Sets, reps and prescribed volume load for a program day.
 *
 * @param {{ exercises?: Array<Object> }} day A `nonDateBasedProgramItems` entry.
 * @returns {{ setCount: number, repCount: number, totalVolumeLoad: number }}
 *   All zero for an empty or missing day.
 */
const getProgramDayTotals = (day) => {
  const exercises = day?.exercises || [];
  return {
    setCount: sumExerciseField(exercises, 'setCount'),
    repCount: sumExerciseField(exercises, 'repCount'),
    totalVolumeLoad: exercises.reduce(
      (sum, exercise) => sum + getExerciseVolumeLoad(exercise),
      0,
    ),
  };
};

export default getProgramDayTotals;
